import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Loader2, Trophy, AlertTriangle, Home, BookOpen, Heart, Settings, Info, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { JourneyStepCard, JourneyStep, UserStepProgress } from "@/components/journey/JourneyStepCard";
import { JourneyVideoStep } from "@/components/journey/JourneyVideoStep";
import { JourneyDocumentUploadStep } from "@/components/journey/JourneyDocumentUploadStep";
import { JourneyWorkflowStep } from "@/components/journey/JourneyWorkflowStep";
import { JourneyAcknowledgmentStep } from "@/components/journey/JourneyAcknowledgmentStep";
import { JourneyFormDataStep } from "@/components/journey/JourneyFormDataStep";
import { JourneyExternalRegistrationStep } from "@/components/journey/JourneyExternalRegistrationStep";
import { JourneySignatureStep } from "@/components/journey/JourneySignatureStep";
import { JourneyOntraportStep } from "@/components/journey/JourneyOntraportStep";
import { JourneyMiniWorkflowStep } from "@/components/journey/JourneyMiniWorkflowStep";
import { JourneyCodeOfConductStep } from "@/components/journey/JourneyCodeOfConductStep";
import { JourneyReadingStep } from "@/components/journey/JourneyReadingStep";
import { JourneyResolutionStep } from "@/components/journey/JourneyResolutionStep";
import { JourneyMemberInvitationStep } from "@/components/journey/JourneyMemberInvitationStep";
import JourneyComingSoonStep from "@/components/journey/JourneyComingSoonStep";
import { JourneyAffiliationStep, AffiliationMode } from "@/components/journey/JourneyAffiliationStep";
import { JourneySectionProgress } from "@/components/journey/JourneySectionProgress";
import { CompletedStepRow } from "@/components/journey/CompletedStepRow";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";
import { cn } from "@/lib/utils";

// Section icon mapping
const sectionIcons = {
  Home,
  BookOpen,
  Heart,
  Settings,
  Trophy,
};

// Extended step interface with section support
interface ExtendedJourneyStep {
  id: string;
  step_number: number;
  title: string;
  description: string | null;
  action_type: string;
  action_config: Record<string, any>;
  estimated_minutes: number | null;
  section_key: string | null;
  section_order: number | null;
  is_active: boolean | null;
  auto_create_resolution: boolean | null;
  ontraport_driven: boolean | null;
}

interface Section {
  id: string;
  section_key: string;
  section_name: string;
  section_order: number;
}

export default function MyJourney() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const devMode = searchParams.get('devMode') === 'true';

  // Section configuration - Updated for new journey structure with descriptions and icons
  const SECTION_CONFIG: Record<string, { 
    title: string; 
    description: string; 
    icon: keyof typeof sectionIcons; 
    order: number 
  }> = {
    welcome: {
      title: 'Welcome',
      description: 'Introduction to your stewardship journey',
      icon: 'Home',
      order: 1
    },
    doctrine_creed_attestation: {
      title: 'Creeds',
      description: 'Establish your religious beliefs and commitments',
      icon: 'BookOpen',
      order: 2
    },
    religious_foundation: {
      title: 'Foundation',
      description: 'Establish your religious practices and center for observance',
      icon: 'Heart',
      order: 3
    },
    governance: {
      title: 'Governance',
      description: 'Set up operational aspects of your UNA',
      icon: 'Settings',
      order: 4
    },
    congratulations: {
      title: 'Complete',
      description: 'Celebration and portal orientation',
      icon: 'Trophy',
      order: 5
    },
    affiliation_agreement: {
      title: 'Affiliation Agreement',
      description: 'Read it, sign it, and send us your signed copy',
      icon: 'BookOpen',
      order: 6
    }
  };


  // Fetch journey steps (only active ones)
  const { data: steps, isLoading: stepsLoading } = useQuery({
    queryKey: ['journey-steps-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('journey_steps')
        .select('*')
        .eq('is_active', true)
        .order('step_number');
      
      if (error) throw error;
      return data as ExtendedJourneyStep[];
    },
  });

  // Derive sections from steps data
  const sections: Section[] = useMemo(() => {
    if (!steps) return [];
    const uniqueSections = [...new Set(steps.map(s => s.section_key))];
    return uniqueSections
      .map(key => ({
        id: key,
        section_key: key,
        section_name: SECTION_CONFIG[key]?.title || key,
        section_order: SECTION_CONFIG[key]?.order || 99,
      }))
      .sort((a, b) => a.section_order - b.section_order);
  }, [steps]);

  // Fetch user progress for all steps
  const { data: progress, isLoading: progressLoading, refetch: refetchProgress } = useQuery({
    queryKey: ['journey-steps-progress', user?.id],
    queryFn: async () => {
      if (!user) return {};
      
      const { data, error } = await supabase
        .from('user_journey_step_progress')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Convert to map by step_id
      const progressMap: Record<string, UserStepProgress> = {};
      data?.forEach(p => {
        progressMap[p.step_id] = {
          id: p.id,
          status: p.status as 'locked' | 'active' | 'completed',
          started_at: p.started_at,
          completed_at: p.completed_at,
          action_data: p.action_data as Record<string, any>,
        };
      });
      
      return progressMap;
    },
    enabled: !!user,
  });

  // Fetch journey state
  const { data: journeyState, refetch: refetchJourneyState } = useQuery({
    queryKey: ['journey-state', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_journey_state')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  // Initialize first step if no progress exists - also check for UNA
  useEffect(() => {
    const initializeProgress = async () => {
      if (!user || !steps?.length || progressLoading) return;

      // Note: role-based redirects belong at ambiguous entry points (root
      // path, post-login landing via pickPreferredHome), not here. An
      // explicit click on "My Journey" from the nav must always render
      // My Journey, including for admin, reviewer, and support accounts
      // (that is also how we test the member flow).



      // SAFETY NET: Check if user has a UNA before proceeding
      const { data: userUna } = await supabase
        .from('unas')
        .select('id')
        .eq('treasury_secretary_id', user.id)
        .maybeSingle();

      if (!userUna) {
        toast.info("Let's set up your UNA first");
        navigate('/confirm-una');
        return;
      }
      
      // Check if user has any progress
      const hasProgress = Object.keys(progress || {}).length > 0;
      
      if (!hasProgress) {
        // Initialize first step as active
        const firstStep = steps[0];
        await supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: user.id,
            step_id: firstStep.id,
            status: 'active',
            started_at: new Date().toISOString(),
          }, { onConflict: 'user_id,step_id' });
        
        // Initialize journey state with current section
        await supabase
          .from('user_journey_state')
          .upsert({
            user_id: user.id,
            current_section: firstStep.section_key || 'admin',
          } as any, { onConflict: 'user_id' });
        
        refetchProgress();
        refetchJourneyState();
      }
    };
    
    initializeProgress();
  }, [user, steps, progress, progressLoading, navigate]);

  // Handle step completion
  const handleStepComplete = () => {
    refetchProgress();
    refetchJourneyState();
    queryClient.invalidateQueries({ queryKey: ['journey-status'] });
  };

  // Calculate overall progress
  const completedSteps = steps?.filter(s => progress?.[s.id]?.status === 'completed').length || 0;
  const totalSteps = steps?.length || 1;
  const overallProgress = Math.round((completedSteps / totalSteps) * 100);

  // Determine current section based on first incomplete step
  const getCurrentSection = (): string => {
    if (!steps || !progress) return 'admin';
    
    // Find first non-completed step
    const firstIncompleteStep = steps.find(s => progress[s.id]?.status !== 'completed');
    return firstIncompleteStep?.section_key || 'congratulations';
  };

  // Get completed sections
  const getCompletedSections = (): string[] => {
    if (!steps || !progress || !sections) return [];
    
    const completedSections: string[] = [];
    
    sections.forEach(section => {
      const sectionSteps = steps.filter(s => s.section_key === section.section_key);
      const allCompleted = sectionSteps.length > 0 && 
        sectionSteps.every(s => progress[s.id]?.status === 'completed');
      
      if (allCompleted) {
        completedSections.push(section.section_key);
      }
    });
    
    return completedSections;
  };

  const currentSection = getCurrentSection();
  const completedSections = getCompletedSections();

  // Track which sections are expanded (controlled state)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set([currentSection]));
  
  // Update expanded sections when currentSection changes - collapse completed sections, expand current
  useEffect(() => {
    setExpandedSections(new Set([currentSection]));
  }, [currentSection, progress]);

  // Guard to prevent double-firing confetti during React re-renders
  const hasFiredConfettiRef = useRef(false);

  // Show confetti when journey completes (only once ever, stored in backend)
  useEffect(() => {
    if (!user?.id) return;
    
    // Check backend field for whether confetti was already shown
    const confettiShownAt = (journeyState as any)?.journey_confetti_shown_at;
    
    // Only fire if: journey completed, 100% progress, not already shown in backend, not already fired this session
    if (
      journeyState?.journey_completed && 
      overallProgress === 100 && 
      !confettiShownAt && 
      !hasFiredConfettiRef.current
    ) {
      hasFiredConfettiRef.current = true;
      
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Mark confetti as shown in backend
      supabase
        .from('user_journey_state')
        .update({ journey_confetti_shown_at: new Date().toISOString() } as any)
        .eq('user_id', user.id)
        .then(() => {
          refetchJourneyState();
        });
    }
  }, [journeyState?.journey_completed, overallProgress, user?.id, journeyState]);

  if (!user) return null;

  if (stepsLoading || progressLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  // Get step progress with defaults - only unlock steps in current section
  const getStepProgress = (stepId: string, stepIndex: number, sectionKey: string): UserStepProgress => {
    if (progress?.[stepId]) {
      return progress[stepId];
    }
    // Only make first step active if it's in the current section
    const isCurrentSection = sectionKey === currentSection;
    return {
      status: (stepIndex === 0 && isCurrentSection) ? 'active' : 'locked',
    };
  };

  // Render step content based on action type
  const renderStepContent = (step: ExtendedJourneyStep, stepProgress: UserStepProgress) => {
    const config = step.action_config;
    const isCompleted = stepProgress.status === 'completed';

    switch (step.action_type) {
      case 'video':
        return (
          <JourneyVideoStep
            stepId={step.id}
            userId={user.id}
            videoUrl={config.video_url}
            secondVideoUrl={config.second_video_url}
            requiredWatchPercent={config.required_watch_percent || config.completion_threshold || 95}
            initialProgress={stepProgress.action_data?.watch_percent || 0}
            initialSecondVideoProgress={stepProgress.action_data?.second_video_percent || 0}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            introText={config.intro_text}
          />
        );
      
      case 'document_upload':
        return (
          <JourneyDocumentUploadStep
            stepId={step.id}
            userId={user.id}
            documentType={config.document_type}
            allowedExtensions={config.allowed_extensions || config.accepted_formats || ['.pdf', '.jpg', '.png']}
            instructions={config.instructions}
            isCompleted={isCompleted}
            existingDocumentUrl={stepProgress.action_data?.document_url}
            onComplete={handleStepComplete}
          />
        );
      
      case 'workflow':
        return (
          <JourneyWorkflowStep
            stepId={step.id}
            userId={user.id}
            workflowKey={config.workflow_key}
            isCompleted={isCompleted}
            actionData={stepProgress.action_data}
            actionConfig={config}
            onComplete={handleStepComplete}
          />
        );

      case 'acknowledgment':
        return (
          <JourneyAcknowledgmentStep
            stepId={step.id}
            userId={user.id}
            content={config.content}
            checkboxText={config.checkbox_text}
            videoUrl={config.video_url}
            showAffiliateLink={config.show_affiliate_link}
            triggersJourneyComplete={config.triggers_journey_complete}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            initialProgress={stepProgress.action_data?.watch_percent || 0}
            videoIntroText={config.video_intro_text}
          />
        );

      case 'form_data_collection':
        // Transform form_fields from database format to component format
        const transformedFields = (config.form_fields || []).map((f: any) => ({
          name: f.field_name,
          label: f.field_label,
          type: f.field_type,
          required: f.is_required,
          placeholder: f.placeholder,
          options: f.field_options,
          conditional_on: f.conditional_on
        }));
        
        // Extract video config if present
        const formVideoUrl = config.video_url;
        const formVideoRequiredPercent = config.required_watch_percent || 95;
        const formInitialVideoProgress = stepProgress.action_data?.video_watch_percent || 0;
        
        return (
          <JourneyFormDataStep
            stepId={step.id}
            userId={user.id}
            fields={transformedFields}
            autoCreateResolution={step.auto_create_resolution || false}
            isCompleted={isCompleted}
            existingData={stepProgress.action_data}
            onComplete={handleStepComplete}
            introText={config.intro_text}
            videoUrl={formVideoUrl}
            videoRequiredWatchPercent={formVideoRequiredPercent}
            initialVideoProgress={formInitialVideoProgress}
          />
        );

      case 'external_registration':
        return (
          <JourneyExternalRegistrationStep
            stepId={step.id}
            userId={user.id}
            registrationUrl={config.registration_url}
            buttonText={config.button_text}
            confirmationText={config.confirmation_text}
            introContent={config.intro_content}
            ontraportTagOnComplete={config.ontraport_tag_on_complete}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
          />
        );

      case 'signature':
        return (
          <JourneySignatureStep
            stepId={step.id}
            userId={user.id}
            documentType={config.document_type}
            documentReady={config.document_ready || false}
            placeholderMessage={config.placeholder_message}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
          />
        );

      case 'ontraport_check':
        const ontraportVideoUrl = config.video_url;
        const ontraportVideoRequiredPercent = config.required_watch_percent || 95;
        const ontraportInitialVideoProgress = stepProgress.action_data?.video_watch_percent || 0;
        
        return (
          <JourneyOntraportStep
            stepId={step.id}
            userId={user.id}
            ontraportTag={config.ontraport_tag}
            tagName={config.tag_name}
            guidanceContent={config.guidance_content}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            videoUrl={ontraportVideoUrl}
            videoRequiredWatchPercent={ontraportVideoRequiredPercent}
            initialVideoProgress={ontraportInitialVideoProgress}
          />
        );

      case 'code_of_conduct_signing':
        return (
          <JourneyCodeOfConductStep
            stepId={step.id}
            userId={user.id}
            documentTitle={config.document_title || 'Code of Doctrine & Discipline'}
            documentType={config.document_type || 'code_of_conduct'}
            documentCategory={config.document_category || 'foundational_documents'}
            templateName={config.template_name || 'Code of Doctrine & Discipline'}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            completedDocumentId={stepProgress.action_data?.document_id}
            introText={config.intro_text}
          />
        );

      case 'mini_workflow_with_signature':
        return (
          <JourneyMiniWorkflowStep
            stepId={step.id}
            userId={user.id}
            templateName={config.template_name}
            documentTitle={config.document_title}
            documentType={config.document_type}
            documentCategory={config.document_category}
            formFields={config.form_fields || []}
            isCompleted={isCompleted}
            existingData={stepProgress.action_data?.form_data}
            onComplete={handleStepComplete}
            videoUrl={config.video_url}
            introConfig={{
              icon: config.intro_icon,
              title: config.intro_title,
              text: config.intro_text,
            }}
            completedDocumentId={stepProgress.action_data?.document_id}
            storeFormDataGlobally={config.store_form_data_globally}
            initialProgress={stepProgress.action_data?.watch_percent || 0}
          />
        );

      case 'reading_acknowledgment':
        return (
          <JourneyReadingStep
            stepId={step.id}
            userId={user.id}
            contentTemplate={config.content_template}
            acknowledgmentText={config.acknowledgment_text || 'I have read and understand this content'}
            videoUrl={config.video_url}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            initialProgress={stepProgress.action_data?.watch_percent || 0}
            videoIntroText={config.video_intro_text}
            readingIntroText={config.reading_intro_text}
            stepTitle={step.title}
            completedDocumentId={stepProgress.action_data?.document_id}
            completedDescriptionText={step.description || undefined}
          />
        );

      case 'resolution_creation':
        return (
          <JourneyResolutionStep
            stepId={step.id}
            userId={user.id}
            resolutionCategory={config.resolution_category}
            resolutionTitle={config.resolution_title}
            autoPopulateFromPreviousStep={config.auto_populate_from_previous_step || false}
            previousStepField={config.previous_step_field}
            isCompleted={isCompleted}
            onComplete={handleStepComplete}
            introText={config.intro_text}
            completedDocumentId={stepProgress.action_data?.document_id}
            completedGovernanceDocumentId={stepProgress.action_data?.governance_document_id}
          />
        );

      case 'member_invitation':
        return (
          <JourneyMemberInvitationStep
            stepId={step.id}
            userId={user.id}
            isCompleted={isCompleted}
            actionConfig={config}
            onComplete={handleStepComplete}
          />
        );

      case 'affiliation_agreement':
        return (
          <JourneyAffiliationStep
            stepId={step.id}
            userId={user.id}
            mode={(config?.mode || 'read') as AffiliationMode}
            documentUrl={config?.document_url}
            documentLabel={config?.document_label}
            documentType={config?.document_type}
            isCompleted={isCompleted}
            actionData={stepProgress.action_data}
            onComplete={handleStepComplete}
          />
        );

      case 'coming_soon':
        return (
          <JourneyComingSoonStep
            userId={user.id}
            slot={config?.slot}
            message={config?.message}
          />
        );

      default:
        return <p className="text-muted-foreground">Unknown step type: {step.action_type}</p>;

    }
  };


  return (
    <MainLayout>
      <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-charcoal">Your Stewardship Journey</h1>
          <p className="text-charcoal/70">
            Complete each step to unlock your full UNA governance tools
          </p>
        </div>

        {/* Overall Progress Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">
                {completedSteps} of {totalSteps} steps completed
              </span>
            </div>
            <Progress value={overallProgress} className="h-3" />
          </CardContent>
        </Card>

        {/* Section Progress */}
        {sections && sections.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <JourneySectionProgress
                sections={sections}
                currentSection={currentSection}
                completedSections={completedSections}
                sectionProgress={sections.reduce((acc, section) => {
                  const sectionSteps = steps?.filter(s => s.section_key === section.section_key) || [];
                  const completedCount = sectionSteps.filter(s => progress?.[s.id]?.status === 'completed').length;
                  acc[section.section_key] = { completed: completedCount, total: sectionSteps.length };
                  return acc;
                }, {} as Record<string, { completed: number; total: number }>)}
              />
            </CardContent>
          </Card>
        )}

        {/* Journey Complete Card */}
        {journeyState?.journey_completed && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Trophy className="w-12 h-12 text-green-600" />
                <div>
                  <h2 className="text-xl font-bold text-green-800">Journey Complete!</h2>
                  <p className="text-green-700">
                    Congratulations! You now have full access to all UNA governance tools.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate('/una-governance')}>
                  Go to UNA Governance
                </Button>
                <Button variant="outline" onClick={() => navigate('/workflows')}>
                  View Workflows
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Your Journey Section - After completion */}
        {journeyState?.journey_completed && (
          <div className="text-center py-2">
            <p className="text-sm text-muted-foreground">
              Review your completed journey steps below
            </p>
          </div>
        )}

        {/* Developer Mode Banner */}
        {devMode && (
          <Card className="bg-amber-50 border-amber-300">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Developer Mode Active</span>
                <span className="text-sm text-amber-600">All {totalSteps} steps are visible</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Migration Notification for users who need to complete new doctrinal steps */}
        {!journeyState?.journey_completed && progress && steps && (() => {
          // Check if user has completed later steps but not the new doctrinal ones (3, 4, 5)
          const doctrineStepIds = ['c59da24f-d5aa-4405-89c6-d92266e4b553', '416c73e9-6ce3-4f97-a305-4ab090f017ae', 'bfec055a-a02f-4128-8dcf-ad5708056eac'];
          const laterStepIds = steps.filter(s => s.step_number >= 10).map(s => s.id);
          
          const hasCompletedLaterSteps = laterStepIds.some(id => progress[id]?.status === 'completed');
          const needsDoctrineSteps = doctrineStepIds.some(id => progress[id]?.status !== 'completed');
          
          if (hasCompletedLaterSteps && needsDoctrineSteps) {
            return (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">Journey Updated</AlertTitle>
                <AlertDescription className="text-blue-700">
                  We've added important doctrinal foundations to your journey. Please complete the Doctrine, Creed, and Personal Attestation steps to continue.
                </AlertDescription>
              </Alert>
            );
          }
          return null;
        })()}

        {/* Normal Mode - Collapsible section cards (also show when journey complete for review) */}
        {!devMode && (
          <div className="space-y-3">
            {sections?.sort((a, b) => a.section_order - b.section_order).map((section) => {
              const sectionSteps = steps?.filter(s => s.section_key === section.section_key) || [];
              if (sectionSteps.length === 0) return null;
              
              const completedCount = sectionSteps.filter(s => progress?.[s.id]?.status === 'completed').length;
              const isCompleted = completedSections.includes(section.section_key);
              const isCurrent = section.section_key === currentSection;
              const isLocked = !isCompleted && !isCurrent;
              const config = SECTION_CONFIG[section.section_key];
              const IconComponent = config ? sectionIcons[config.icon] : Home;
              
              // Don't render locked sections (unless journey is complete - then show all)
              if (isLocked && !journeyState?.journey_completed) return null;
              
              const isExpanded = expandedSections.has(section.section_key);
              
              const toggleSection = () => {
                setExpandedSections(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(section.section_key)) {
                    newSet.delete(section.section_key);
                  } else {
                    newSet.add(section.section_key);
                  }
                  return newSet;
                });
              };
              
              return (
                <Collapsible key={section.id} open={isExpanded} onOpenChange={toggleSection}>
                  <div className={cn(
                    "rounded-lg overflow-hidden",
                    isCompleted && "border border-green-200",
                    isCurrent && "border border-primary/30"
                  )}>
                    {/* Collapsible Section Header */}
                    <CollapsibleTrigger asChild>
                      <button className={cn(
                        "w-full flex items-center justify-between p-4 transition-colors",
                        isCompleted && "bg-green-50 hover:bg-green-100/80",
                        isCurrent && "bg-primary/5 hover:bg-primary/10"
                      )}>
                        <div className="flex items-center gap-3">
                          <IconComponent className={cn(
                            "w-6 h-6",
                            isCompleted && "text-green-600",
                            isCurrent && "text-charcoal"
                          )} />
                          <h2 className={cn(
                            "text-lg font-semibold text-left",
                            isCompleted && "text-green-700",
                            isCurrent && "text-charcoal"
                          )}>
                            {config?.title || section.section_name}
                          </h2>
                        </div>
                        <ChevronDown className={cn(
                          "w-5 h-5 transition-transform duration-200",
                          isCompleted && "text-green-600",
                          isCurrent && "text-charcoal",
                          isExpanded && "rotate-180"
                        )} />
                      </button>
                    </CollapsibleTrigger>
                    
                    {/* Collapsible Content - Steps */}
                    <CollapsibleContent>
                      <div className={cn(
                        "p-4 pt-0 space-y-3",
                        isCompleted && "bg-green-50/50",
                        isCurrent && "bg-primary/5"
                      )}>
                        {sectionSteps.map((step, index) => {
                          const stepProgress = getStepProgress(step.id, index, section.section_key);
                          const hasActiveStepInSection = sectionSteps.some(s => progress?.[s.id]?.status === 'active');
                          const isFirstIncomplete = !hasActiveStepInSection && index === completedCount && isCurrent;
                          const isStepActive = stepProgress.status === 'active' || isFirstIncomplete;
                          
                          return (
                            <JourneyStepCard
                              key={step.id}
                              step={step}
                              progress={stepProgress}
                              isActive={isStepActive && isCurrent}
                              defaultCollapsed={stepProgress.status === 'locked'}
                              hideDescriptionWhenCompleted={step.action_type === 'reading_acknowledgment'}
                            >
                              {renderStepContent(step, stepProgress)}
                            </JourneyStepCard>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        )}

        {/* Developer Mode - Show ALL steps from ALL sections */}
        {devMode && (
          <div className="space-y-6">
            {sections?.sort((a, b) => a.section_order - b.section_order).map((section) => {
              const sectionSteps = steps?.filter(s => s.section_key === section.section_key) || [];
              if (sectionSteps.length === 0) return null;
              
              const completedCount = sectionSteps.filter(s => progress?.[s.id]?.status === 'completed').length;
              const config = SECTION_CONFIG[section.section_key];
              const IconComponent = config ? sectionIcons[config.icon] : Home;
              
              return (
                <div key={section.id} className="space-y-3">
                  {/* Section Header with icon, title, description */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50/50 border border-amber-200">
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-6 h-6 text-amber-700" />
                      <div>
                        <h2 className="text-lg font-semibold text-amber-800">
                          {config?.title || section.section_name}
                        </h2>
                        <p className="text-sm text-amber-600">
                          {config?.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                      {completedCount} of {sectionSteps.length} complete
                    </div>
                  </div>

                  {/* All Steps in this Section */}
                  {sectionSteps.map((step, index) => {
                    const stepProgress = getStepProgress(step.id, index, section.section_key);
                    return (
                      <JourneyStepCard
                        key={step.id}
                        step={step}
                        progress={stepProgress}
                        isActive={true} // Force all to be expandable in dev mode
                      >
                        {renderStepContent(step, stepProgress)}
                      </JourneyStepCard>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </MainLayout>
  );
}
